import React, { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useDebounce from "../hooks/useDebounce";
import configServ from "../../services/config";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { EditTwoTone, PersonAdd, VisibilityTwoTone } from "@mui/icons-material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useLocation } from "react-router-dom";
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  NativeSelect,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  Chip,
} from "@mui/material";
import SearchBox from "../Search/SearchBox";
import {
  cellStyle,
  evenRowStyle,
  headerCellStyle,
  oddRowStyle,
  rowStyle,
} from "../../utility/CustomStyle";
import AllEnquiries from "./AllEnquiries";
import {
  rSetCustomer,
  rSetEnquiry,
} from "../../redux/reducers/GlobalVariables";
import bell_notification from "../Assets/images/bell_notification.gif";
import { hideLoader, showLoader } from "../../redux/reducers/LoaderTrigger";
import Swal from "sweetalert2";
import EnquiryDetailsDialog from "./EnquiryDetailsDialog";
import CancelIcon from "@mui/icons-material/Cancel";
// FUNCTION
const EnquiryTable = ({ isChanged, handleEdit }) => {
  //#region code
  const dispatch = useDispatch();
  const { company_id, companyAddress, companyState, companyCountry, user_id } =
    useSelector((state) => state.GlobalVariables);
  const rowsPerPageOptions = [10, 20, 50, 100];
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [count, setCount] = useState(0);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("id");
  const [enquiryList, setEnquiryList] = useState([]);
  const [searchBy, setSearchBy] = useState("name");
  const [search, setSearch] = useState(null);
  const debounceSearch = useDebounce(search, 1000);
  const [selected, setSelected] = useState(null);
  const headers = [
    { id: "registration_no", label: "REG. NO." },
    { id: "name", label: "NAME" },
    { id: "email", label: "EMAIL" },
    { id: "father_name", label: "FATHER NAME" },
    { id: "marital_status", label: "MARITAL STATUS" },
    { id: "gender", label: "Gender" },

    { id: "status", label: "STATUS" },
  ];
  const location = useLocation();
  const [status, setStatus] = useState("pending");

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
  };

  useEffect(() => {
    if (location.state?.status) {
      setStatus(location.state.status);
    }
  }, [location.state]);

  const fetchEnquiryList = async () => {
    try {
      setLoading(true);
      const dataToSend = {
        company_id: company_id,
        page: page,
        per_page: rowsPerPage,
        search: search,
        search_by: searchBy,
        status: status,
      };
      const res = await configServ.getEnquiries(dataToSend);
      if (res.status === 200) {
        setEnquiryList(res.data);
        setCount(res.total);
      } else {
        setEnquiryList([]);
        setCount(0);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiryList();
  }, [page]);

  useEffect(() => {
    if (page === 0) {
      fetchEnquiryList();
    } else {
      setPage(0);
    }
  }, [isChanged, rowsPerPage, debounceSearch, status]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchBy = (event) => {
    try {
      setSearchBy(event.target.value);
    } catch (error) {
      console.log(error);
    }
  };

  const handleApproved = async (params) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to approve this enquiry?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Approve",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          dispatch(showLoader());
          const dataToSend = {
            id: params.id,
            company_id: company_id,
            user_id: user_id,
          };
          const res = await configServ.convertEnquiryToCustomer(dataToSend);

          if (res.status === 200) {
            Swal.fire({
              icon: "success",
              title: "Approved!",
              text: res.message || "Member created successfully.",
            });
            fetchEnquiryList();
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: res.message || "Failed to create customer.",
            });
          }
        } catch (e) {
          console.error(e);
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Something went wrong!",
          });
        } finally {
          dispatch(hideLoader());
        }
      }
    });
  };

  const handleOpen = (params) => {
    setSelected(params);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleReject = async (params) => {
    Swal.fire({
      title: "Reject Enquiry",
      text: "Once you reject, you cannot convert this enquiry into a customer again. This action cannot be reverted!",
      input: "textarea",
      inputLabel: "Please provide remarks",
      inputPlaceholder: "Type your remarks here...",
      inputAttributes: {
        "aria-label": "Type your remarks here",
      },
      showCancelButton: true,
      confirmButtonText: "Reject",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      preConfirm: (remarks) => {
        if (!remarks) {
          Swal.showValidationMessage("Remarks are required!");
        }
        return remarks;
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          dispatch(showLoader());

          const dataToSend = {
            id: params.id,
            company_id: company_id,
            user_id: user_id,
            remarks: result.value, // remarks user ke input se
          };

          const res = await configServ.rejectEnquiry(dataToSend);

          if (res.status === 200) {
            Swal.fire({
              icon: "success",
              title: "Rejected!",
              text: res.message || "Enquiry has been rejected successfully.",
            });
            fetchEnquiryList();
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: res.message || "Failed to reject enquiry.",
            });
          }
        } catch (e) {
          console.error(e);
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Something went wrong!",
          });
        } finally {
          dispatch(hideLoader());
        }
      }
    });
  };

  //#endregion

  return (
    <>
      {/* <AllEnquiries open={open} handleClose={handleClose} /> */}
      <EnquiryDetailsDialog
        open={open}
        onClose={handleClose}
        data={selected}
        onApprove={handleApproved}
        onReject={handleReject}
      />

      <Box width={"100%"} sx={{ flexGrow: 1, overflow: "auto", mt: 1 }}>
        <Grid
          container
          alignItems="flex-end"
          justifyContent="flex-start"
          spacing={1}
        >
          <Grid container item xs={12} md={3} justifyContent={"flex-start"}>
            <Typography sx={{ fontSize: "18px", fontWeight: "bold" }}>
              Members Enquiries: {count}
            </Typography>
          </Grid>

          {/* New Dropdown for Status */}
          <Grid item xs={12} md={3}>
            <Box sx={{ minWidth: 120 }}>
              <FormControl fullWidth>
                <InputLabel variant="standard" htmlFor="status-select">
                  Status
                </InputLabel>
                <NativeSelect
                  value={status}
                  onChange={handleStatusChange}
                  inputProps={{
                    name: "status",
                    id: "status-select",
                  }}
                >
                  <option value={"pending"}>Pending</option>
                  <option value={"approved"}>Approved</option>
                  <option value={"rejected"}>Rejected</option>
                  <option value={"all"}>All</option>
                </NativeSelect>
              </FormControl>
            </Box>
          </Grid>

          {/* Existing Search By Dropdown */}
          <Grid item xs={12} md={3}>
            <Box sx={{ minWidth: 120 }}>
              <FormControl fullWidth>
                <InputLabel variant="standard" htmlFor="uncontrolled-native">
                  Search By
                </InputLabel>
                <NativeSelect
                  value={searchBy}
                  onChange={handleSearchBy}
                  inputProps={{
                    name: "searchBy",
                    id: "uncontrolled-native",
                  }}
                >
                  <option value={"name"}>Name</option>
                  <option value={"email"}>Email</option>
                </NativeSelect>
              </FormControl>
            </Box>
          </Grid>

          {/* Search Box */}
          <Grid item xs={12} md={3}>
            <SearchBox search={search} setSearch={setSearch} />
          </Grid>
        </Grid>
      </Box>

      {/* TABLE VIEW (md+ devices) */}
      <Box
        width={"100%"}
        sx={{
          flexGrow: 1,
          overflow: "auto",
          mt: 1,
          display: { xs: "none", md: "block" },
        }}
      >
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellStyle}>S.No.</TableCell>
                {headers.map((column) => (
                  <TableCell
                    key={column.id}
                    sortDirection={orderBy === column.id ? order : false}
                    sx={headerCellStyle}
                  >
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell sx={headerCellStyle}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading === false ? (
                enquiryList.length > 0 ? (
                  enquiryList.map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      sx={{
                        ...rowStyle,
                        ...(rowIndex % 2 === 0 ? evenRowStyle : oddRowStyle),
                      }}
                    >
                      <TableCell sx={cellStyle}>
                        <Stack direction={"row"} alignItems={"center"} mx={1}>
                          <Typography variant="body1">
                            {page * rowsPerPage + rowIndex + 1}
                          </Typography>
                          {row.status === "pending" && (
                            <img
                              src={bell_notification}
                              alt=""
                              width={30}
                              style={{ cursor: "pointer" }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        {row.registration_no || ""}
                      </TableCell>
                      <TableCell sx={cellStyle}>{row.name}</TableCell>
                      <TableCell sx={cellStyle}>{row.email || ""}</TableCell>
                      <TableCell sx={cellStyle}>
                        {row.father_name || ""}
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        {row.marital_status || ""}
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        {row.gender || ""}
                      </TableCell>
                      <TableCell sx={cellStyle}>{row.status || ""}</TableCell>

                      <TableCell sx={cellStyle}>
                        <Stack direction={"row"}>
                          {/* <Tooltip title="Approved" placement="top">
                            <IconButton
                              sx={{ cursor: "pointer" }}
                              onClick={() => handleApproved(row)}
                              disabled={
                                row.status === "approved" ||
                                row.status === "rejected"
                              }
                            >
                              <CheckCircleIcon
                                sx={{
                                  color:
                                    row.status === "approved"
                                      ? "green" // approved = green
                                      : row.status === "rejected"
                                      ? "red" // rejected = red
                                      : "primary.main", // pending ya default = primary
                                }}
                              />
                            </IconButton>
                          </Tooltip> */}

                          <Tooltip title="Details" placement="top">
                            <IconButton sx={{ cursor: "pointer" }}>
                              <VisibilityTwoTone
                                color="info"
                                onClick={() => handleOpen(row)}
                                sx={{ cursor: "pointer" }}
                              />
                            </IconButton>
                          </Tooltip>
                          {/* <Tooltip title="Reject" placement="top">
                            <IconButton
                              size="small"
                              onClick={() => handleReject(row)}
                              disabled={
                                row.status === "approved" ||
                                row.status === "rejected"
                              }
                              sx={{ cursor: "pointer" }}
                            >
                              <CancelIcon
                                sx={{
                                  color:
                                    row.status === "rejected"
                                      ? "red" // rejected = red
                                      : row.status === "approved"
                                      ? "green" // approved = green
                                      : "primary.main", // pending/other = blue
                                }}
                              />
                            </IconButton>
                          </Tooltip> */}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={12}>
                      <Box textAlign={"center"}>
                        <Typography variant="body1" color={"red"}>
                          No data found.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              ) : (
                <TableRow>
                  <TableCell colSpan={12} sx={{ textAlign: "center" }}>
                    <Box>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>

      {/* CARD VIEW (xs devices) */}
      <Box sx={{ display: { xs: "block", md: "none" }, mt: 1 }}>
        {loading === false ? (
          enquiryList.length > 0 ? (
            enquiryList.map((row, rowIndex) => (
              <Box
                key={rowIndex}
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                  boxShadow: 1,
                  bgcolor: "background.paper",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {row.name}
                  </Typography>
                  <Chip
                    label={row.status}
                    size="small"
                    color={
                      row.status === "approved"
                        ? "success"
                        : row.status === "pending"
                        ? "warning"
                        : "default"
                    }
                  />
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  Reg No: {row.registration_no || "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Email: {row.email || "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Father: {row.father_name || "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Marital: {row.marital_status || "—"}
                </Typography>
                 <Typography variant="body2" color="text.secondary">
                  Gender: {row.gender || "—"}
                </Typography>

                <Stack direction="row" spacing={1} mt={1}>
                  {/* <Tooltip title="Approved" placement="top">
                    <IconButton
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleApproved(row)}
                      disabled={
                        row.status === "approved" || row.status === "rejected"
                      }
                    >
                      <CheckCircleIcon
                        sx={{
                          color:
                            row.status === "approved"
                              ? "green" // approved = green
                              : row.status === "rejected"
                              ? "red" // rejected = red
                              : "primary.main", // pending ya default = primary
                        }}
                      />
                    </IconButton>
                  </Tooltip> */}

                  <Tooltip title="Details" placement="top">
                    <IconButton sx={{ cursor: "pointer" }}>
                      <VisibilityTwoTone
                        color="info"
                        onClick={() => handleOpen(row)}
                        sx={{ cursor: "pointer" }}
                      />
                    </IconButton>
                  </Tooltip>
                  {/* <Tooltip title="Reject" placement="top">
                    <IconButton
                      size="small"
                      onClick={() => handleReject(row)}
                      disabled={
                        row.status === "approved" || row.status === "rejected"
                      }
                      sx={{ cursor: "pointer" }}
                    >
                      <CancelIcon
                        sx={{
                          color:
                            row.status === "rejected"
                              ? "red" // rejected = red
                              : row.status === "approved"
                              ? "green" // approved = green
                              : "primary.main", // pending/other = blue
                        }}
                      />
                    </IconButton>
                  </Tooltip> */}
                </Stack>
              </Box>
            ))
          ) : (
            <Box textAlign={"center"}>
              <Typography variant="body1" color={"red"}>
                No data found.
              </Typography>
            </Box>
          )
        ) : (
          <Box textAlign={"center"} py={2}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </>
  );
};

export default memo(EnquiryTable);
